import { useMutation, useReactiveVar } from '@apollo/client';
import { AnalyticIdentifier, createError, capitalCase } from '@common/neo.tax';
import * as Sentry from '@sentry/react';
import {
  Button,
  colors,
  FileDropZone,
  FileMimeType,
  InputFormContainer,
  ModalHeader,
  Stack,
  TitleWithHelp,
  ControlledAutocompleteInput,
} from '@ui-components/neo.tax';
import { ReactComponent as PlusCircle } from '@ui-components/neo.tax/dist/icons/plus-circle.svg';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { HELP_EXPORT_TICKETS_URL } from '@neo.tax/shared/constants';
import {
  AddCompanyBulkTicketingDataFileMutation,
  AddCompanyBulkTicketingDataFileMutationVariables,
  ADD_COMPANY_BULK_TICKETING_DATA_FILE,
  GetOneSchemaJwtMutation,
  GetOneSchemaJwtMutationVariables,
  GET_ONE_SCHEMA_JWT,
  GetTicketingDataSignedUploadUrlMutation,
  GetTicketingDataSignedUploadUrlMutationVariables,
  GET_TICKETING_DATA_SIGNED_UPLOAD_URL,
  FileUploadTicketingProvider,
  TicketingEntityType,
  UploadAction,
} from '@neo.tax/shared/graphql';
import { useCurrentCompanyOrFail, useUserFromAuth, useCurrentUser } from '@neo.tax/shared/hooks';
import { FileUploadsState, ResumableFileUpload, useAnalyticId } from '@neo.tax/shared/state';
import { getTicketingProviderName, initiateResumableFileUpload } from '@neo.tax/shared/utils';

import { StyledButton, StyledFileDisplaySection, TicketingFileUploadModalStyled } from './ticketing-file-upload.styles';
import { TICKETING_PROVIDER_CONFIG } from './ticketing-provider-config';

type OneSchemaParameters = {
  oneSchemaJwt: string;
  permanentUrl: string;
  signedUrl: string;
  ticketingEntityType: TicketingEntityType;
  ticketingProvider: FileUploadTicketingProvider;
};

type TicketingFileUploadModalProps = {
  isOpen: boolean;
  close: () => void;
  showOneSchema: (oneSchemaParameters: OneSchemaParameters) => void;
};

type TicketingFileUploadForm = {
  ticketingProvider: FileUploadTicketingProvider | null;
  ticketingEntityType: TicketingEntityType | null;
};

type FormStep = 'provider' | 'entityType' | 'upload';

const COMPANY_DOCUMENT_UPLOAD_TAG = 'COMPANY_DOCUMENT_UPLOAD_TAG';

export const TicketingFileUploadModal = ({
  close,
  isOpen,
  showOneSchema,
}: TicketingFileUploadModalProps): React.ReactElement => {
  const ticketingProviderInputId = useAnalyticId(AnalyticIdentifier.FILE_UPLOAD_TICKETING_PROVIDER_INPUT);
  const ticketingEntityTypeInputId = useAnalyticId(AnalyticIdentifier.FILE_UPLOAD_TICKETING_ENTITY_TYPE_INPUT);
  const addCompanyBulkTicketingDataFileUploadId = useAnalyticId(
    AnalyticIdentifier.COMPANY_BULK_TICKETING_DATA_FILE_UPLOAD,
  );
  const [getOneSchemaJwt] = useMutation<GetOneSchemaJwtMutation, GetOneSchemaJwtMutationVariables>(GET_ONE_SCHEMA_JWT);
  const company = useCurrentCompanyOrFail();
  const { isSuperUserMode } = useUserFromAuth();
  const filteredTicketingProviders = useMemo(
    () =>
      Object.values(FileUploadTicketingProvider).filter(
        (value) =>
          isSuperUserMode ||
          [
            FileUploadTicketingProvider.JiraCloud,
            FileUploadTicketingProvider.AzureDevops,
            FileUploadTicketingProvider.Other,
          ].includes(value),
      ),
    [isSuperUserMode],
  );

  const { user } = useCurrentUser();

  // including the company ID allows this to continue working even when you switch companies
  const companyDocumentUploadTag = `${COMPANY_DOCUMENT_UPLOAD_TAG}-${company.id}`;

  // Using this state to get a more responsive UI when the upload button is clicked
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [currentStep, setCurrentStep] = useState<FormStep>('provider');

  const { control, handleSubmit, reset, setValue } = useForm<TicketingFileUploadForm>({
    mode: 'onChange',
    defaultValues: {
      ticketingProvider: null,
      ticketingEntityType: null,
    },
  });

  const selectedProvider = useWatch({ control, name: 'ticketingProvider' });
  const selectedEntityType = useWatch({ control, name: 'ticketingEntityType' });

  // Reset entity type when provider changes (only if entity type not yet selected)
  useEffect(() => {
    if (selectedProvider && !selectedEntityType) {
      setCurrentStep('entityType');
    }
  }, [selectedProvider, selectedEntityType, setValue]);

  // Move to upload step when entity type is selected
  useEffect(() => {
    if (selectedProvider && selectedEntityType) {
      setCurrentStep('upload');
    }
  }, [selectedProvider, selectedEntityType]);

  // Get available entity types for selected provider
  const availableEntityTypes = useMemo(() => {
    if (!selectedProvider) return [];
    const config = TICKETING_PROVIDER_CONFIG[selectedProvider];
    return config ? config.entityTypes : [];
  }, [selectedProvider]);

  // Check if current provider should show file upload zone
  const shouldShowFileDropZone = useMemo(() => {
    if (!selectedProvider) return false;
    const config = TICKETING_PROVIDER_CONFIG[selectedProvider];
    return config ? config.showFileDropZone : true;
  }, [selectedProvider]);

  const [getUploadUrlMutation] = useMutation<
    GetTicketingDataSignedUploadUrlMutation,
    GetTicketingDataSignedUploadUrlMutationVariables
  >(GET_TICKETING_DATA_SIGNED_UPLOAD_URL);

  const [addCompanyBulkTicketingDataFile, { loading: loadingAddCompanyBulkTicketingDataFile }] = useMutation<
    AddCompanyBulkTicketingDataFileMutation,
    AddCompanyBulkTicketingDataFileMutationVariables
  >(ADD_COMPANY_BULK_TICKETING_DATA_FILE);

  useReactiveVar(FileUploadsState.fileUploadsVar); // subscribe re-render on file upload state change

  const filesForUpload = FileUploadsState.getFilesWithUploadProgressForDisplaySection(companyDocumentUploadTag);
  const isUploading = loadingAddCompanyBulkTicketingDataFile || FileUploadsState.isUploading(companyDocumentUploadTag);

  const clearForm = useCallback((): void => {
    setCurrentStep('provider');
    reset({
      ticketingProvider: null,
      ticketingEntityType: null,
    });
    // Clear all files for this tag
    const filesToRemove = FileUploadsState.getFileUploads(companyDocumentUploadTag);
    filesToRemove.forEach((fileUpload) => {
      if (!FileUploadsState.isUploading(fileUpload.file.name)) {
        FileUploadsState.removeFileUpload(fileUpload.file.name);
      }
    });
  }, [reset, setCurrentStep, companyDocumentUploadTag]);

  useEffect(() => {
    if (!isOpen) {
      setDisableSubmit(false);
      clearForm();
    }
  }, [isOpen, clearForm]);

  const resetToEntityType = (): void => {
    setCurrentStep('entityType');
    setValue('ticketingEntityType', null);
    // Clear files when going back to entity type selection
    const filesToRemove = FileUploadsState.getFileUploads(companyDocumentUploadTag);
    filesToRemove.forEach((fileUpload) => {
      if (!FileUploadsState.isUploading(fileUpload.file.name)) {
        FileUploadsState.removeFileUpload(fileUpload.file.name);
      }
    });
  };

  const addFiles = async (files: File[]): Promise<void> => {
    await FileUploadsState.addManyFileUploads(files, companyDocumentUploadTag);
  };

  const triggerResumableFileUpload = async (
    fileUpload: ResumableFileUpload,
    ticketingProvider: FileUploadTicketingProvider,
    ticketingEntityType: TicketingEntityType,
  ): Promise<void> => {
    await initiateResumableFileUpload({
      fileUpload,
      getUploadUrl: async () => {
        // get a signed url from our backend to upload the file to
        const uploadUrlResult = await getUploadUrlMutation({
          variables: { fileName: fileUpload.file.name, companyId: company.id, action: UploadAction.Resumable },
        });
        const { permanentUrl, signedUrl } = uploadUrlResult.data?.getTicketingDataSignedUploadUrl ?? {};
        if (!signedUrl || !permanentUrl || uploadUrlResult.errors) {
          Sentry.addBreadcrumb({ level: 'error', data: { uploadUrlResult } });
          throw createError('FileUploadError', 'An unexpected error happened while fetching the signed url.');
        }
        return { permanentUrl, signedUrl };
      },
      onFinishedUploading: async (permanentUrl) => {
        await addCompanyBulkTicketingDataFile({
          variables: {
            companyId: company.id,
            input: { permanentUrl, ticketingProvider: ticketingProvider!, ticketingEntityType: ticketingEntityType! },
          },
          optimisticResponse: {
            addCompanyBulkTicketingDataFile: {
              __typename: 'Company',
              id: company.id,
              bulkTicketingDataUploads: [
                ...company.bulkTicketingDataUploads,
                {
                  __typename: 'BulkTicketingDataFileUpload',
                  id: 'temp-id',
                  createdAt: new Date().toISOString(),
                  fileName: fileUpload.file.name,
                  signedUrl: 'temp-url',
                  ticketingProvider: ticketingProvider!,
                  ticketingEntityType: ticketingEntityType!,
                  isValid: null,
                },
              ],
            },
          },
        });
      },
    });
  };

  const onSubmitResumableFileUpload = handleSubmit(
    async ({ ticketingEntityType, ticketingProvider }: TicketingFileUploadForm): Promise<void> => {
      if (isUploading) {
        return;
      }

      if (!ticketingProvider || !ticketingEntityType) {
        Sentry.addBreadcrumb({
          level: 'error',
          data: { ticketingProvider, ticketingEntityType },
        });
        throw createError('FileUploadError', 'Ticketing provider or entity type not set at time of uploading files.');
      }

      setDisableSubmit(true);
      const fileUploads = FileUploadsState.getFileUploads(companyDocumentUploadTag);
      await Promise.all([
        ...fileUploads.map((fileUpload) =>
          triggerResumableFileUpload(fileUpload, ticketingProvider, ticketingEntityType),
        ),
      ]);
      setDisableSubmit(false);
      clearForm();
      close();
    },
  );

  const onSubmitOneSchema = handleSubmit(
    async ({ ticketingEntityType, ticketingProvider }: TicketingFileUploadForm): Promise<void> => {
      if (!user) {
        Sentry.addBreadcrumb({
          level: 'error',
          data: { user },
        });
        throw createError('FileUploadError', 'User not set at time of loading one schema.');
      }
      if (!ticketingProvider || !ticketingEntityType) {
        Sentry.addBreadcrumb({
          level: 'error',
          data: { ticketingProvider, ticketingEntityType },
        });
        throw createError(
          'FileUploadError',
          'Ticketing provider or entity type not set at time of loading one schema.',
        );
      }

      setDisableSubmit(true);
      const fileName = `${ticketingProvider}-${ticketingEntityType}.csv`;
      const { data: uploadUrlData, errors: uploadUrlErrors } = await getUploadUrlMutation({
        variables: { companyId: company.id, fileName, action: UploadAction.Write },
      });
      const { permanentUrl, signedUrl } = uploadUrlData?.getTicketingDataSignedUploadUrl ?? {};

      if (!permanentUrl || !signedUrl || uploadUrlErrors) {
        Sentry.addBreadcrumb({
          level: 'error',
          data: { signedUrl, permanentUrl, ticketingEntityType, ticketingProvider, uploadUrlErrors },
        });
        throw createError('FileUploadError', 'An unexpected error happened while fetching the signed url.');
      }
      const { data: oneSchemaJwtData, errors: oneSchemaJwtErrors } = await getOneSchemaJwt({
        variables: { userId: user.id },
      });
      const oneSchemaJwt = oneSchemaJwtData?.getOneSchemaJwt;

      if (!oneSchemaJwt || oneSchemaJwtErrors) {
        Sentry.addBreadcrumb({
          level: 'error',
          data: { oneSchemaJwt, oneSchemaJwtErrors },
        });
        throw createError('FileUploadError', 'An unexpected error happened while fetching the one schema JWT.');
      }

      showOneSchema({
        oneSchemaJwt,
        permanentUrl,
        signedUrl,
        ticketingEntityType,
        ticketingProvider,
      });
    },
  );

  const hasFileErrors = useMemo(() => filesForUpload.some((file) => file.status === 'error'), [filesForUpload]);

  // Check if steps should be disabled (locked)
  const isProviderLocked = selectedEntityType !== null;
  const isEntityTypeLocked = currentStep === 'upload' && shouldShowFileDropZone;

  // Calculate modal height for progressive display
  const modalHeight = useMemo(() => {
    const baseHeight = 250;
    let dynamicHeight = 0;

    // Always include provider step height
    dynamicHeight += filteredTicketingProviders.length * 15;

    // Add entity type step height if visible
    if (currentStep === 'entityType' || currentStep === 'upload') {
      dynamicHeight += 120;
    }

    // Add upload step height if visible
    if (currentStep === 'upload' && shouldShowFileDropZone) {
      dynamicHeight += 300;
    }

    return baseHeight + dynamicHeight;
  }, [currentStep, filteredTicketingProviders.length, shouldShowFileDropZone]);

  return (
    <TicketingFileUploadModalStyled isOpen={isOpen}>
      <Stack as="form" direction="column" space={4} style={{ height: modalHeight }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <ModalHeader
            icon={<PlusCircle width={48} height={48} fill={colors.blue[600]} />}
            iconBgColor={colors.white}
            title={<TitleWithHelp title="Upload Ticketing Files" href={HELP_EXPORT_TICKETS_URL} />}
          />

          {/* Step 1: Provider Selection - Always visible */}
          <ControlledAutocompleteInput
            control={control}
            name="ticketingProvider"
            label="Select Your Ticketing Provider"
            id={ticketingProviderInputId}
            options={filteredTicketingProviders.map((value) => ({
              value,
              label: getTicketingProviderName(value),
            }))}
            disabled={isProviderLocked}
            action={
              isProviderLocked ? (
                <Button
                  variant="white"
                  size="sm"
                  onClick={() => {
                    clearForm();
                  }}
                >
                  Change
                </Button>
              ) : undefined
            }
          />

          {/* Step 2: Entity Type Selection - Visible once provider is selected */}
          {(currentStep === 'entityType' || currentStep === 'upload') && selectedProvider ? (
            <ControlledAutocompleteInput
              control={control}
              name="ticketingEntityType"
              label={`Select the data type being uploaded for ${getTicketingProviderName(selectedProvider)}`}
              id={ticketingEntityTypeInputId}
              options={availableEntityTypes.map((value: TicketingEntityType) => ({
                value,
                label: capitalCase(value),
              }))}
              disabled={isEntityTypeLocked}
              action={
                isEntityTypeLocked ? (
                  <Button
                    variant="white"
                    size="sm"
                    onClick={() => {
                      resetToEntityType();
                    }}
                  >
                    Change
                  </Button>
                ) : undefined
              }
            />
          ) : null}

          {/* Step 3: File Upload - Visible once entity type is selected and file upload is needed */}
          {currentStep === 'upload' && shouldShowFileDropZone ? (
            <InputFormContainer
              label={`${getTicketingProviderName(selectedProvider!)} ${capitalCase(selectedEntityType!)}`}
              inputId={addCompanyBulkTicketingDataFileUploadId}
            >
              <FileDropZone
                allowedMimeTypes={[
                  FileMimeType.GZIP,
                  FileMimeType.CSV,
                  FileMimeType.EXCEL,
                  FileMimeType.JSON,
                  FileMimeType.TEXT_PLAIN,
                ]}
                addFiles={addFiles}
                text="files"
                allowMultipleSelect
              />
              <StyledFileDisplaySection files={filesForUpload} removeFile={FileUploadsState.removeFileUpload} />
            </InputFormContainer>
          ) : null}
        </div>

        <Stack direction="row" space={4}>
          <StyledButton
            variant="white"
            rounded="regular"
            size="base"
            onClick={(): void => {
              clearForm();
              close();
            }}
          >
            Cancel
          </StyledButton>

          {currentStep === 'upload' && shouldShowFileDropZone ? (
            <StyledButton
              variant="primary"
              rounded="regular"
              size="base"
              onClick={onSubmitResumableFileUpload}
              disabled={filesForUpload.length === 0 || disableSubmit || hasFileErrors || !user}
            >
              {FileUploadsState.isUploading(companyDocumentUploadTag)
                ? 'Uploading...'
                : loadingAddCompanyBulkTicketingDataFile
                  ? 'Finishing up...'
                  : 'Upload Files'}
            </StyledButton>
          ) : !shouldShowFileDropZone &&
            selectedProvider &&
            (currentStep === 'entityType' || currentStep === 'upload') ? (
            <StyledButton
              variant="primary"
              rounded="regular"
              size="base"
              onClick={async () => {
                await onSubmitOneSchema();
              }}
              disabled={!selectedEntityType || disableSubmit}
            >
              {disableSubmit ? 'Loading...' : 'Continue'}
            </StyledButton>
          ) : (
            <></>
          )}
        </Stack>
      </Stack>
    </TicketingFileUploadModalStyled>
  );
};
